// AdminMegaDropdown — F9.5 WAVE 2E · wide multi-column dropdown that replaces
// both the legacy sidebar and the compact UserMenuDropdown.
//
// Wave Marketplace V10 · MP.6 extensions:
//   • Fetch /api/v2/marketplace/creator/me on open · set isCreator state
//   • Hide items with creatorOnly: true if not creator
//   • Hide items with nonCreatorOnly: true if creator
//   • Badge counts: fetch unread notifications + pending moderation on open
//
// Props: `user` → from AuthContext (name, email, role, picture, …).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronDown, LogOut, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
import { API } from "@/lib/api";
import { MENU_GROUPS } from "./menuGroups";

async function _apiJson(path) {
    try {
        const r = await fetch(`${API}${path}`, { credentials: "include" });
        if (!r.ok) return null;
        return await r.json();
    } catch {
        return null;
    }
}

export default function AdminMegaDropdown({ user }) {
    const { t } = useTranslation("header_v3");
    const { logout, hasModule } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    // MP.6 · creator profile + badges state
    const [isCreator, setIsCreator] = useState(null); // null = unknown
    const [badges, setBadges] = useState({});

    const isAdmin = isAdminRole(user?.role);

    const refreshContext = useCallback(async () => {
        // Creator profile check
        const me = await _apiJson("/v2/marketplace/creator/me");
        setIsCreator(Boolean(me && me.creator));
        // Badges · creator notifications unread
        const b = {};
        if (me && me.creator) {
            const n = await _apiJson("/v2/marketplace/creator/notifications");
            if (n && typeof n.unread === "number") b.creator_notifications_unread = n.unread;
        }
        if (isAdmin) {
            const q = await _apiJson("/v2/marketplace/admin/moderation/queue?limit=1");
            if (q && q.counts && typeof q.counts.pending === "number") {
                b.admin_moderation_pending = q.counts.pending;
            }
        }
        setBadges(b);
    }, [isAdmin]);

    useEffect(() => {
        if (!open) return;
        refreshContext();
    }, [open, refreshContext]);

    // Close on outside click + ESC
    // Keyboard ESC only. The backdrop (rendered inside the portal) already
    // handles click-outside to dismiss. We deliberately avoid a document-level
    // mousedown handler because the panel is portaled into document.body (not
    // inside `wrapperRef`), which would cause clicks on menu items to be seen
    // as "outside" → the panel would close before the <Link> navigation ran.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);

    const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
    const displayName = user?.name || user?.email?.split("@")[0] || "user";

    // Primary CTA: single trigger button; `data-testid` targets automation.
    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label={t("admin_menu.trigger_aria")}
                className="flex items-center gap-2 pr-2 pl-1 py-1 rounded-full hover:bg-white/[0.06] transition-colors"
                data-testid="admin-dropdown-trigger"
            >
                <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0"
                    style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #CCCCCC 100%)" }}
                    aria-hidden
                >
                    {initials}
                </div>
                <span className="hidden md:inline text-sm text-white/95 max-w-[110px] truncate">
                    {displayName}
                </span>
                <ChevronDown
                    className={`w-3.5 h-3.5 text-[#CCCCCC] transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    {open && (
                        <React.Fragment key="admin-dropdown-portal">
                            {/* Backdrop (dim + blur) — desktop subtle, mobile stronger */}
                            <motion.div
                                key="admin-dropdown-backdrop"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.14 }}
                                className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm md:bg-black/30"
                                onClick={() => setOpen(false)}
                                aria-hidden
                                data-testid="admin-dropdown-backdrop"
                            />

                            {/* Panel — responsive: desktop wide floating, mobile full-screen */}
                            <motion.div
                                key="admin-dropdown-panel"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                                className="
                                    fixed z-[70] overflow-hidden flex flex-col
                                    border border-white/5 shadow-[0_28px_80px_rgba(0,0,0,0.7)]
                                    inset-0 rounded-none
                                    md:inset-auto md:top-16 md:right-4
                                    md:w-[min(760px,calc(100vw-2rem))] md:rounded-2xl
                                    md:max-h-[85vh]
                                "
                                style={{ backgroundColor: "#0A0A0A" }}
                                role="menu"
                                aria-label={t("admin_menu.panel_aria")}
                                data-testid="admin-dropdown-panel"
                            >
                            {/* Mobile close affordance */}
                            <div className="md:hidden flex items-center justify-end px-4 pt-3 pb-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="h-9 px-3 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/[0.06]"
                                    data-testid="admin-dropdown-close-mobile"
                                >
                                    {t("admin_menu.close")}
                                </button>
                            </div>

                            {/* User header (sticky) */}
                            <div className="px-5 py-4 border-b border-white/5 shrink-0 flex items-center gap-3">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0"
                                    style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #CCCCCC 100%)" }}
                                    aria-hidden
                                >
                                    {initials}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-base font-semibold text-white truncate" data-testid="admin-dropdown-name">
                                        {displayName}
                                    </div>
                                    {user?.email && (
                                        <div className="text-xs text-[#CCCCCC] truncate" data-testid="admin-dropdown-email">
                                            {user.email}
                                        </div>
                                    )}
                                </div>
                                {isAdmin && (
                                    <span
                                        className="shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                                        style={{ backgroundColor: "rgba(255, 255, 255,0.18)", color: "#FFFFFF" }}
                                        data-testid="admin-dropdown-badge"
                                    >
                                        {t("admin_menu.admin_badge")}
                                    </span>
                                )}
                            </div>

                            {/* Groups grid (scrollable) */}
                            <div className="flex-1 overflow-y-auto px-5 py-5">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                                    {MENU_GROUPS.filter((g) => !g.adminOnly || isAdmin).map((group) => {
                                        // MP.6 · filter group items by creator conditional + adminOnly
                                        const visibleItems = group.items.filter((it) => {
                                            if (it.adminOnly && !isAdmin) return false;
                                            if (it.creatorOnly && isCreator !== true) return false;
                                            if (it.nonCreatorOnly && isCreator === true) return false;
                                            return true;
                                        });
                                        if (visibleItems.length === 0) return null;
                                        return (
                                        <section key={group.key} data-testid={`admin-menu-group-${group.key}`}>
                                            <h3
                                                className="text-[10px] uppercase tracking-wider font-semibold text-[#CCCCCC] mb-2.5 px-1"
                                            >
                                                {group.headerKey ? t(group.headerKey) : (group.label || group.key)}
                                            </h3>
                                            <ul className="space-y-0.5" role="group">
                                                {visibleItems.map((item) => {
                                                    const locked = !isAdmin && item.module && !hasModule(item.module);
                                                    const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                                                    const Icon = item.icon;
                                                    const badgeCount = item.badge ? badges[item.badge] : 0;
                                                    const common = {
                                                        "data-testid": `admin-menu-item-${item.key}`,
                                                        role: "menuitem",
                                                    };
                                                    const base = "group flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors";
                                                    if (locked) {
                                                        return (
                                                            <li key={item.key}>
                                                                <span
                                                                    {...common}
                                                                    aria-disabled="true"
                                                                    className={`${base} text-[#999999] cursor-not-allowed`}
                                                                >
                                                                    <Icon className="w-4 h-4 opacity-60" />
                                                                    <span className="flex-1 truncate">{item.labelKey ? t(item.labelKey) : (item.label || item.key)}</span>
                                                                    <Lock className="w-3 h-3" aria-hidden />
                                                                </span>
                                                            </li>
                                                        );
                                                    }
                                                    return (
                                                        <li key={item.key} className={item.hero ? "pb-1.5 mb-1.5 border-b border-white/10" : undefined}>
                                                            <Link
                                                                {...common}
                                                                to={item.to}
                                                                onClick={() => setOpen(false)}
                                                                className={
                                                                    active
                                                                        ? `${base} font-medium`
                                                                        : `${base} ${item.hero ? "text-white font-semibold" : "text-[#E5E5E5]"} hover:text-white hover:bg-white/[0.06]`
                                                                }
                                                                style={
                                                                    active
                                                                        ? { backgroundColor: "rgba(255, 255, 255,0.12)", color: "#FFFFFF", borderLeft: "2px solid #FFFFFF" }
                                                                        : undefined
                                                                }
                                                                aria-current={active ? "page" : undefined}
                                                            >
                                                                <Icon className="w-4 h-4" style={active ? { color: "#FFFFFF" } : undefined} />
                                                                <span className="flex-1 truncate">{item.labelKey ? t(item.labelKey) : (item.label || item.key)}</span>
                                                                {badgeCount > 0 && (
                                                                    <span
                                                                        data-testid={`admin-menu-badge-${item.key}`}
                                                                        className="ml-1 rounded-full border border-white/10 bg-white/[0.08] px-1.5 py-0.5 text-[10px] font-medium text-[#F5F5F5]"
                                                                    >
                                                                        {badgeCount > 99 ? "99+" : badgeCount}
                                                                    </span>
                                                                )}
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </section>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Logout (sticky bottom) */}
                            <div className="shrink-0 border-t border-white/5 px-5 py-3">
                                <button
                                    type="button"
                                    onClick={async () => { setOpen(false); await logout(); }}
                                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-[#F87171] hover:text-neutral-300 hover:bg-neutral-500/10 transition-colors"
                                    data-testid="admin-dropdown-logout"
                                    role="menuitem"
                                >
                                    <LogOut className="w-4 h-4" />
                                    <span>{t("admin_menu.logout")}</span>
                                </button>
                            </div>
                            </motion.div>
                        </React.Fragment>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
}
